import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, useLocation } from 'wouter';
import { ArrowLeft, Trash2, Loader2, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';

interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  title: string | null;
  status: string;
  person_type: string;
  created_at: string;
  updated_at: string | null;
  accounts?: Array<{
    accountId: string;
    accountName: string;
    jobTitle: string | null;
    isPrimary: number;
    department: string | null;
  }>;
}

export default function ContactDetailPage() {
  const { sessionId } = useAuth();
  const { id } = useParams();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: contact, isLoading } = useQuery<Contact>({
    queryKey: ['/api/contacts', id, sessionId],
    queryFn: async () => {
      return await api.get(`/contacts/${id}`, sessionId);
    },
    enabled: !!id && !!sessionId,
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return await api.delete(`/contacts/${id}`, sessionId);
    },
    onSuccess: () => {
      toast({
        title: 'Contact deleted',
        description: 'The contact has been deleted successfully.',
      });
      setLocation('/contacts');
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete contact',
        variant: 'destructive',
      });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <User className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h2 className="text-lg font-medium">Contact not found</h2>
        <p className="text-muted-foreground text-sm mt-1">
          The contact you're looking for doesn't exist or has been deleted.
        </p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => setLocation('/contacts')}
        >
          Back to Contacts
        </Button>
      </div>
    );
  }

  const accounts = contact.accounts || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setLocation('/contacts')}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold">
            {contact.first_name} {contact.last_name}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-6">
          <div className="rounded-lg border p-6">
            <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-muted-foreground">Name</dt>
                <dd className="text-sm font-medium">
                  {contact.first_name} {contact.last_name}
                </dd>
              </div>
              {contact.title && (
                <div>
                  <dt className="text-sm text-muted-foreground">Title</dt>
                  <dd className="text-sm font-medium">{contact.title}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm text-muted-foreground">Status</dt>
                <dd className="text-sm font-medium">
                  <Badge variant={contact.status === 'ACTIVE' ? 'default' : 'secondary'}>
                    {contact.status}
                  </Badge>
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">Type</dt>
                <dd className="text-sm font-medium">{contact.person_type}</dd>
              </div>
            </dl>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border p-6">
            <h2 className="text-lg font-semibold mb-4">Associated Accounts</h2>
            {accounts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No accounts associated</p>
            ) : (
              <div className="space-y-3">
                {accounts.map((acc, idx) => (
                  <div
                    key={idx}
                    className="flex items-start justify-between p-3 rounded-md border"
                  >
                    <div>
                      <div className="font-medium">{acc.accountName}</div>
                      {acc.jobTitle && (
                        <div className="text-sm text-muted-foreground">{acc.jobTitle}</div>
                      )}
                      {acc.department && (
                        <div className="text-sm text-muted-foreground">{acc.department}</div>
                      )}
                    </div>
                    {acc.isPrimary === 1 && (
                      <Badge variant="secondary" className="text-xs">Primary</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Contact</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {contact.first_name} {contact.last_name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
